import { useCallback, useEffect, useRef, useState } from 'react';

import { PLAYER_STATE_EVENTS } from '../constants';
import {
  generateConfigFromTracks,
  handleAutoPlay,
  handleToggleMuted,
  handleTogglePlaying,
} from '../logic';

const useMediaPlayerControls = ({
  initialState,
  tracks,
  additionalConfig,
  onStateChange,
  onRegisterPlayer,
  onSkip,
  onComplete,
}) => {
  const playerRef = useRef();
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(initialState.muted ?? false);
  const [config, setConfig] = useState(
    generateConfigFromTracks(tracks, additionalConfig),
  );

  const fireStateChangeEvent = useCallback(
    (event, value) => {
      onStateChange(event, value);
    },
    [onStateChange],
  );

  useEffect(() => {
    fireStateChangeEvent(PLAYER_STATE_EVENTS.PLAYING, playing);
  }, [playing, fireStateChangeEvent]);

  useEffect(() => {
    fireStateChangeEvent(PLAYER_STATE_EVENTS.MUTED, muted);
  }, [muted, fireStateChangeEvent]);

  const handleSkip = useCallback(() => {
    onSkip();
    fireStateChangeEvent(PLAYER_STATE_EVENTS.SKIPPED, true);
  }, [onSkip, fireStateChangeEvent]);

  const handleComplete = useCallback(() => {
    onComplete();
    fireStateChangeEvent(PLAYER_STATE_EVENTS.COMPLETED, true);
  }, [onComplete, fireStateChangeEvent]);

  const togglePlaying = useCallback(() => {
    handleTogglePlaying(playing, setPlaying);
  }, [playing, setPlaying]);

  const toggleMuted = useCallback(() => {
    handleToggleMuted(muted, setMuted);
  }, [muted, setMuted]);

  const stopPlaying = useCallback(() => {
    setPlaying(false);
  }, [setPlaying]);

  const onReady = useCallback(() => {
    fireStateChangeEvent(PLAYER_STATE_EVENTS.READY, true);
    handleAutoPlay(
      initialState.playing,
      initialState.muted,
      playerRef?.current,
      setPlaying,
      setMuted,
    );
  }, [setPlaying, fireStateChangeEvent]);

  const onPlay = useCallback(() => {
    setPlaying(true);
  }, [setPlaying]);

  const onRestart = useCallback(() => {
    playerRef?.current.seekTo(0, 'seconds');
  }, []);

  useEffect(() => {
    onRegisterPlayer?.({
      onPlay,
      onPause: stopPlaying,
      onRestart,
    });
  }, [onPlay, stopPlaying, onRestart]);

  return {
    fireStateChangeEvent,
    config,
    muted,
    onReady,
    onPlay,
    handleSkip,
    handleComplete,
    playerRef,
    playing,
    showCC: muted,
    stopPlaying,
    toggleMuted,
    togglePlaying,
  };
};

export default useMediaPlayerControls;
